import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Issue } from '../../data/mockData';
import { useProjectContext } from '../../hooks/useProjectContext';
import { Bug, BookOpen, Zap, Layers, Plus, MoreHorizontal, ExternalLink, Loader2, Check, AlertTriangle, EyeOff, Eye } from 'lucide-react';
import { useBrandConfig } from '../../hooks/useBrandConfig';
import { RefreshButton } from '../shared/RefreshButton';
import { runTool } from '../../services/toolRunner';
import { useGithubData } from '../../hooks/useGithubData';
import { findLinkedGitData } from '../../services/issueGitLinker';
import { GitLinkedBadges } from '../shared/GitLinkedBadges';
import { useBoardColumns, findColumnForStatus, BoardColumn, clearBoardColumnsCache } from '../../hooks/useBoardColumns';
import { BoardFilters, BoardFilterState, defaultFilters } from '../shared/BoardFilters';
import { IssueDetailPanel } from './IssueDetailPanel';
type KanbanStatus = 'To Do' | 'In Progress' | 'Review' | 'Done';
// Fallback hardcoded columns (used when Jira columns are not available)
const FALLBACK_COLUMNS: {
  id: KanbanStatus;
  label: string;
  color: string;
  bg: string;
  count_color: string;
}[] = [{
  id: 'To Do',
  label: 'Por Hacer',
  color: 'text-slate-400',
  bg: 'bg-slate-800/30',
  count_color: 'bg-slate-700 text-slate-400'
}, {
  id: 'In Progress',
  label: 'En Progreso',
  color: 'text-blue-400',
  bg: 'bg-blue-950/20',
  count_color: 'bg-blue-900/50 text-blue-400'
}, {
  id: 'Review',
  label: 'En Revisión',
  color: 'text-amber-400',
  bg: 'bg-amber-950/20',
  count_color: 'bg-amber-900/50 text-amber-400'
}, {
  id: 'Done',
  label: 'Completado',
  color: 'text-emerald-400',
  bg: 'bg-emerald-950/20',
  count_color: 'bg-emerald-900/50 text-emerald-400'
}];
// Color palette for dynamic columns (cycles through these)
const DYNAMIC_COLUMN_COLORS = [{
  color: 'text-slate-400',
  bg: 'bg-slate-800/30',
  count_color: 'bg-slate-700 text-slate-400'
}, {
  color: 'text-blue-400',
  bg: 'bg-blue-950/20',
  count_color: 'bg-blue-900/50 text-blue-400'
}, {
  color: 'text-cyan-400',
  bg: 'bg-cyan-950/20',
  count_color: 'bg-cyan-900/50 text-cyan-400'
}, {
  color: 'text-amber-400',
  bg: 'bg-amber-950/20',
  count_color: 'bg-amber-900/50 text-amber-400'
}, {
  color: 'text-violet-400',
  bg: 'bg-violet-950/20',
  count_color: 'bg-violet-900/50 text-violet-400'
}, {
  color: 'text-emerald-400',
  bg: 'bg-emerald-950/20',
  count_color: 'bg-emerald-900/50 text-emerald-400'
}, {
  color: 'text-rose-400',
  bg: 'bg-rose-950/20',
  count_color: 'bg-rose-900/50 text-rose-400'
}, {
  color: 'text-orange-400',
  bg: 'bg-orange-950/20',
  count_color: 'bg-orange-900/50 text-orange-400'
}];
// Known column name patterns → specific colors
const KNOWN_COLUMN_STYLES: Record<string, {
  color: string;
  bg: string;
  count_color: string;
}> = {
  backlog: {
    color: 'text-violet-400',
    bg: 'bg-violet-950/20',
    count_color: 'bg-violet-900/50 text-violet-400'
  },
  'to do': {
    color: 'text-slate-400',
    bg: 'bg-slate-800/30',
    count_color: 'bg-slate-700 text-slate-400'
  },
  'por hacer': {
    color: 'text-slate-400',
    bg: 'bg-slate-800/30',
    count_color: 'bg-slate-700 text-slate-400'
  },
  'in progress': {
    color: 'text-blue-400',
    bg: 'bg-blue-950/20',
    count_color: 'bg-blue-900/50 text-blue-400'
  },
  'en progreso': {
    color: 'text-blue-400',
    bg: 'bg-blue-950/20',
    count_color: 'bg-blue-900/50 text-blue-400'
  },
  development: {
    color: 'text-blue-400',
    bg: 'bg-blue-950/20',
    count_color: 'bg-blue-900/50 text-blue-400'
  },
  review: {
    color: 'text-amber-400',
    bg: 'bg-amber-950/20',
    count_color: 'bg-amber-900/50 text-amber-400'
  },
  'in review': {
    color: 'text-amber-400',
    bg: 'bg-amber-950/20',
    count_color: 'bg-amber-900/50 text-amber-400'
  },
  'code review': {
    color: 'text-amber-400',
    bg: 'bg-amber-950/20',
    count_color: 'bg-amber-900/50 text-amber-400'
  },
  qa: {
    color: 'text-cyan-400',
    bg: 'bg-cyan-950/20',
    count_color: 'bg-cyan-900/50 text-cyan-400'
  },
  testing: {
    color: 'text-cyan-400',
    bg: 'bg-cyan-950/20',
    count_color: 'bg-cyan-900/50 text-cyan-400'
  },
  done: {
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/20',
    count_color: 'bg-emerald-900/50 text-emerald-400'
  },
  completado: {
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/20',
    count_color: 'bg-emerald-900/50 text-emerald-400'
  },
  closed: {
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/20',
    count_color: 'bg-emerald-900/50 text-emerald-400'
  }
};
function getColumnStyle(name: string, index: number) {
  const known = KNOWN_COLUMN_STYLES[name.toLowerCase()];
  if (known) return known;
  return DYNAMIC_COLUMN_COLORS[index % DYNAMIC_COLUMN_COLORS.length];
}
/** Effective column used for rendering — works for both Jira and fallback */
interface EffectiveColumn {
  id: string;
  label: string;
  color: string;
  bg: string;
  count_color: string;
  /** Status names that belong to this column (for matching issues) */
  statusNames: string[];
}
// Status keywords to match Jira transitions (kept for drag & drop)
const STATUS_KEYWORDS: Record<KanbanStatus, string[]> = {
  'To Do': ['to do', 'todo', 'backlog', 'open', 'new', 'reopen', 'por hacer'],
  'In Progress': ['in progress', 'start', 'progress', 'en progreso', 'development'],
  Review: ['review', 'in review', 'code review', 'revisión', 'qa', 'testing'],
  Done: ['done', 'closed', 'resolved', 'complete', 'completado', 'finish']
};
function getTypeIcon(type: string) {
  switch (type) {
    case 'Bug':
      return <Bug size={11} className="text-red-400" data-id="element-5117" />;
    case 'Story':
      return <BookOpen size={11} className="text-emerald-400" data-id="element-5118" />;
    case 'Task':
      return <Zap size={11} className="text-blue-400" data-id="element-5119" />;
    case 'Epic':
      return <Layers size={11} className="text-violet-400" data-id="element-5120" />;
    default:
      return null;
  }
}
const AVATAR_COLORS = ['bg-indigo-900/50 border-indigo-800 text-indigo-300', 'bg-emerald-900/50 border-emerald-800 text-emerald-300', 'bg-amber-900/50 border-amber-800 text-amber-300', 'bg-rose-900/50 border-rose-800 text-rose-300', 'bg-cyan-900/50 border-cyan-800 text-cyan-300', 'bg-violet-900/50 border-violet-800 text-violet-300', 'bg-orange-900/50 border-orange-800 text-orange-300', 'bg-pink-900/50 border-pink-800 text-pink-300'];
/** Deterministic color based on name hash */
function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
function getPriorityDot(priority: string) {
  switch (priority) {
    case 'High':
      return 'bg-red-400';
    case 'Medium':
      return 'bg-amber-400';
    case 'Low':
      return 'bg-blue-400';
    default:
      return 'bg-slate-500';
  }
}
function findMatchingTransition(transitions: any[], targetStatus: KanbanStatus): any | null {
  const keywords = STATUS_KEYWORDS[targetStatus];
  return transitions.find((t: any) => {
    const name = (t.name || '').toLowerCase();
    const toName = (t.to?.name || '').toLowerCase();
    return keywords.some(kw => name.includes(kw) || toName.includes(kw));
  }) || null;
}
type TransitionState = {
  issueKey: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
};
interface KanbanCardProps {
  issue: Issue;
  onDragStart: (e: React.DragEvent, issueKey: string) => void;
  onClick?: () => void;
  jiraBaseUrl: string;
  transitionState?: TransitionState;
  linkedGitData?: import('../../services/issueGitLinker').LinkedGitData;
}
/** Detect if an issue only has ID data (not enriched yet) */
function isSkeletonIssue(issue: Issue): boolean {
  // If summary is just the key or starts with #, it's not enriched
  if (!issue.summary) return true;
  if (issue.summary === issue.key) return true;
  if (issue.summary.startsWith('#') && issue.summary.replace('#', '') === issue.key.replace('#', '')) return true;
  // If summary is just a number (the Jira ID), it's not enriched
  if (/^\d+$/.test(issue.summary)) return true;
  return false;
}
/** Skeleton card shown while issue data is loading */
function SkeletonCard({
  issueKey
}: {
  issueKey: string;
}) {
  return <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 animate-pulse" data-id="element-5121">
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-2" data-id="element-5122">
        <div className="w-3 h-3 rounded bg-slate-700" data-id="element-5123" />
        <span className="text-[10px] font-mono text-slate-600" data-id="element-5124">{issueKey}</span>
      </div>
      {/* Summary skeleton */}
      <div className="space-y-1.5 mb-3" data-id="element-5125">
        <div className="h-3.5 bg-slate-800 rounded w-full" data-id="element-5126" />
        <div className="h-3.5 bg-slate-800 rounded w-2/3" data-id="element-5127" />
      </div>
      {/* Footer skeleton */}
      <div className="flex items-center justify-between" data-id="element-5128">
        <div className="flex items-center gap-1.5" data-id="element-5129">
          <div className="w-1.5 h-1.5 rounded-full bg-slate-700" data-id="element-5130" />
          <div className="w-8 h-4 bg-slate-800 rounded" data-id="element-5131" />
        </div>
        <div className="w-6 h-6 rounded-full bg-slate-800" data-id="element-5132" />
      </div>
    </div>;
}
function KanbanCard({
  issue,
  onDragStart,
  onClick,
  jiraBaseUrl,
  transitionState,
  linkedGitData
}: KanbanCardProps) {
  const isPending = transitionState?.status === 'pending';
  const isSuccess = transitionState?.status === 'success';
  const isError = transitionState?.status === 'error';
  return <div draggable={!isPending} onDragStart={e => onDragStart(e, issue.key)} onClick={onClick} className={`bg-slate-900 border rounded-lg p-3 transition-all group select-none shadow-sm relative ${isPending ? 'border-indigo-500/50 opacity-80 cursor-wait' : isSuccess ? 'border-emerald-500/50 cursor-grab active:cursor-grabbing' : isError ? 'border-red-500/50 cursor-grab active:cursor-grabbing' : 'border-slate-800 hover:border-slate-600 cursor-grab active:cursor-grabbing'}`} data-id="element-5133">
      {/* Transition status indicator */}
      {transitionState && <div className={`absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-white shadow-lg ${isPending ? 'bg-indigo-600 animate-spin' : isSuccess ? 'bg-emerald-600 animate-in fade-in zoom-in duration-300' : 'bg-red-600 animate-in fade-in zoom-in duration-300'}`} data-id="element-5134">
          {isPending ? <Loader2 size={10} data-id="element-5135" /> : isSuccess ? <Check size={10} data-id="element-5136" /> : <AlertTriangle size={10} data-id="element-5137" />}
        </div>}

      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2" data-id="element-5138">
        <div className="flex items-center gap-1.5" data-id="element-5139">
          {getTypeIcon(issue.type)}
          <span className="text-[10px] font-mono text-slate-500" data-id="element-5140">
            {issue.key}
          </span>
        </div>
        <a href={`${jiraBaseUrl}/${issue.key}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-indigo-400" data-id="element-5141">
          <ExternalLink size={11} data-id="element-5142" />
        </a>
      </div>

      {/* Summary */}
      <p className="text-sm text-slate-200 leading-snug mb-3 group-hover:text-indigo-300 transition-colors" data-id="element-5143">
        {issue.summary}
      </p>

      {/* Labels */}
      {issue.labels && issue.labels.length > 0 && <div className="flex flex-wrap gap-1 mb-3" data-id="element-5144">
          {issue.labels.slice(0, 2).map(label => <span key={label} className="px-1.5 py-0.5 bg-slate-800 text-slate-500 rounded text-[10px] border border-slate-700" data-id="element-5145">
              {label}
            </span>)}
        </div>}

      {/* Git linked badges */}
      {linkedGitData && (linkedGitData.prs.length > 0 || linkedGitData.commits.length > 0 || linkedGitData.branches.length > 0) && <div className="mb-2" data-id="element-5146">
            <GitLinkedBadges linked={linkedGitData} compact data-id="element-5147" />
          </div>}

      {/* Footer */}
      <div className="flex items-center justify-between" data-id="element-5148">
        <div className="flex items-center gap-1.5" data-id="element-5149">
          <div className={`w-1.5 h-1.5 rounded-full ${getPriorityDot(issue.priority)}`} title={issue.priority} data-id="element-5150" />

          {issue.points && <span className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded border border-slate-700" data-id="element-5151">
              {issue.points}p
            </span>}
        </div>
        {issue.assignee && <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-bold ${getAvatarColor(issue.assignee.name)}`} title={issue.assignee.name} data-id="element-5152">
            {issue.assignee.initials}
          </div>}
      </div>
    </div>;
}
interface KanbanBoardProps {
  boardId?: number | null;
  boardName?: string;
}
export function KanbanBoard({
  boardId,
  boardName
}: KanbanBoardProps) {
  const {
    brand
  } = useBrandConfig();
  const {
    activeProject,
    dataMode
  } = useProjectContext();
  const isLive = dataMode === 'live';
  const {
    prs: githubPrs,
    commits: githubCommits,
    branches: githubBranches
  } = useGithubData(activeProject?.key);
  // Fetch real board columns from Jira
  const {
    columns: jiraColumns,
    loading: columnsLoading,
    isFromJira
  } = useBoardColumns(isLive && boardId ? boardId : null);
  // Hidden columns state
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const toggleColumnVisibility = (colId: string) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(colId)) {
        next.delete(colId);
      } else {
        next.add(colId);
      }
      return next;
    });
  };
  // Build effective columns: Jira columns or fallback
  const effectiveColumns: EffectiveColumn[] = useMemo(() => {
    if (jiraColumns && jiraColumns.length > 0) {
      return jiraColumns.map((col, idx) => ({
        id: col.name,
        label: col.name,
        statusNames: col.statusNames,
        ...getColumnStyle(col.name, idx)
      }));
    }
    // Fallback: use hardcoded columns with keyword-based matching
    return FALLBACK_COLUMNS.map(col => ({
      ...col,
      statusNames: STATUS_KEYWORDS[col.id] || [col.id.toLowerCase()]
    }));
  }, [jiraColumns]);
  // Visible columns (filtered by hidden state)
  const visibleColumns = effectiveColumns.filter(col => !hiddenColumns.has(col.id));
  const hiddenColumnsList = effectiveColumns.filter(col => hiddenColumns.has(col.id));
  // Combine sprint issues + backlog for full board view
  const allIssues = useMemo(() => {
    const seen = new Set<string>();
    const combined: Issue[] = [];
    const issues = Array.isArray(activeProject?.issues) ? activeProject.issues : [];
    const backlog = Array.isArray(activeProject?.backlog) ? activeProject.backlog : [];
    for (const issue of [...issues, ...backlog]) {
      if (!seen.has(issue.key)) {
        seen.add(issue.key);
        combined.push(issue);
      }
    }
    return combined;
  }, [activeProject?.issues, activeProject?.backlog]);
  const [boardIssues, setBoardIssues] = useState<Issue[]>(allIssues);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [transitionStates, setTransitionStates] = useState<Record<string, TransitionState>>({});
  // Filter state
  const [filters, setFilters] = useState<BoardFilterState>(defaultFilters);
  // Apply filters to boardIssues
  const filteredIssues = useMemo(() => {
    return boardIssues.filter(issue => {
      // Search
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!issue.summary.toLowerCase().includes(q) && !issue.key.toLowerCase().includes(q)) {
          return false;
        }
      }
      // Assignee
      if (filters.assignees.length > 0) {
        if (!issue.assignee || !filters.assignees.includes(issue.assignee.name)) {
          return false;
        }
      }
      // Priority
      if (filters.priorities.length > 0) {
        if (!filters.priorities.includes(issue.priority)) {
          return false;
        }
      }
      // Type
      if (filters.types.length > 0) {
        if (!filters.types.includes(issue.type)) {
          return false;
        }
      }
      return true;
    });
  }, [boardIssues, filters]);
  // Sync board issues when project changes
  useEffect(() => {
    const seen = new Set<string>();
    const combined: Issue[] = [];
    const issues = Array.isArray(activeProject?.issues) ? activeProject.issues : [];
    const backlog = Array.isArray(activeProject?.backlog) ? activeProject.backlog : [];
    for (const issue of [...issues, ...backlog]) {
      if (!seen.has(issue.key)) {
        seen.add(issue.key);
        combined.push(issue);
      }
    }
    setBoardIssues(combined);
  }, [activeProject?.issues, activeProject?.backlog]);
  // Track enrichment progress
  const [enrichingCount, setEnrichingCount] = useState(0);
  // Track which issues have already been enriched to avoid re-fetching
  const enrichedKeysRef = useRef<Set<string>>(new Set());
  // Enrich issues with real Jira status when we have Jira columns
  // This fixes the mismatch where search_issues returns only IDs,
  // causing issues to get default "To Do" status instead of their real status
  useEffect(() => {
    if (!isLive || !isFromJira || !effectiveColumns.length || boardIssues.length === 0) return;
    // Build set of ALL real status names from Jira columns
    const allJiraStatuses = new Set<string>();
    effectiveColumns.forEach(col => {
      col.statusNames.forEach(s => allJiraStatuses.add(s.toLowerCase()));
    });
    // Find issues that need enrichment:
    // 1. Missing rawStatus or rawStatus doesn't match any Jira column status
    // 2. Missing summary (skeleton issues — only have ID)
    const needsEnrichment = boardIssues.filter(issue => {
      if (enrichedKeysRef.current.has(issue.key)) return false;
      // Skeleton issue — no real data at all
      if (isSkeletonIssue(issue)) return true;
      if (!issue.rawStatus) return true;
      const raw = issue.rawStatus.toLowerCase();
      // If rawStatus matches a real Jira status, it's already good
      if (allJiraStatuses.has(raw)) return false;
      // If rawStatus is one of the simplified defaults, it was probably not real
      const simplified = ['to do', 'in progress', 'review', 'done', 'backlog'];
      if (simplified.includes(raw)) return true;
      return false;
    });
    if (needsEnrichment.length === 0) return;
    console.log(`[Kanban] Enriching ${needsEnrichment.length} issues with real Jira status...`);
    setEnrichingCount(needsEnrichment.length);
    // Mark these as being enriched to prevent re-runs
    needsEnrichment.forEach(i => enrichedKeysRef.current.add(i.key));
    let cancelled = false;
    const enrichIssues = async () => {
      const enriched = new Map<string, Partial<Issue>>(); // key → enriched fields
      const batchSize = 5;
      for (let i = 0; i < needsEnrichment.length; i += batchSize) {
        if (cancelled) return;
        const batch = needsEnrichment.slice(i, i + batchSize);
        const results = await Promise.all(batch.map(async issue => {
          const issueId = issue.key.startsWith('#') ? issue.key.replace('#', '') : issue.key;
          try {
            const result = await runTool('get-issue', {
              issue_key: issueId
            });
            if (result.success && result.data) {
              const data = result.data;
              const fields = data.fields || data;
              const enrichedData: Partial<Issue> & {
                _origKey: string;
              } = {
                _origKey: issue.key
              } as any;
              // Status (critical for column matching)
              const realStatus = fields.status?.name || fields.status;
              if (realStatus && typeof realStatus === 'string') {
                enrichedData.rawStatus = realStatus;
              }
              // Summary
              if (fields.summary && typeof fields.summary === 'string') {
                enrichedData.summary = fields.summary;
              }
              // Priority
              const pName = fields.priority?.name || fields.priority;
              if (pName && typeof pName === 'string') {
                enrichedData.priority = pName as Issue['priority'];
              }
              // Type
              const tName = fields.issuetype?.name || fields.issueType?.name || fields.type;
              if (tName && typeof tName === 'string') {
                enrichedData.type = tName as Issue['type'];
              }
              // Assignee
              if (fields.assignee) {
                const aName = fields.assignee.displayName || fields.assignee.name || '';
                if (aName) {
                  const parts = aName.split(' ');
                  enrichedData.assignee = {
                    name: aName,
                    initials: parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : aName.slice(0, 2).toUpperCase(),
                    avatar: fields.assignee.avatarUrls?.['24x24'] || ''
                  };
                }
              }
              // Story points
              const points = fields.story_points || fields.customfield_10016 || fields.story_point_estimate;
              if (typeof points === 'number') {
                enrichedData.points = points;
              }
              // Labels
              if (Array.isArray(fields.labels) && fields.labels.length > 0) {
                enrichedData.labels = fields.labels;
              }
              // Real Jira key
              const jiraKey = data.key || fields.key;
              if (jiraKey && typeof jiraKey === 'string') {
                enrichedData.key = jiraKey;
              }
              return enrichedData;
            }
          } catch (e) {
            console.warn(`[Kanban] Failed to enrich ${issueId}:`, e);
          }
          return null;
        }));
        results.forEach((r: any) => {
          if (r) {
            const origKey = r._origKey || r.key;
            delete r._origKey;
            enriched.set(origKey, r);
          }
        });
        // Progressive update: apply enriched data as each batch completes
        if (!cancelled && enriched.size > 0) {
          const snapshot = new Map(enriched);
          setBoardIssues(prev => prev.map(issue => {
            const data = snapshot.get(issue.key);
            if (data) return {
              ...issue,
              ...data
            };
            return issue;
          }));
          setEnrichingCount(Math.max(0, needsEnrichment.length - enriched.size));
        }
      }
      if (cancelled) return;
      console.log(`[Kanban] ✓ Enriched ${enriched.size} issues with full data`);
      setEnrichingCount(0);
    };
    enrichIssues();
    return () => {
      cancelled = true;
    };
  }, [isLive, isFromJira, effectiveColumns, boardIssues.length]);
  // Note: we use boardIssues.length as dep (not boardIssues) to avoid infinite loops
  // since this effect updates boardIssues. enrichedKeysRef prevents re-fetching.
  // Reset enriched keys when project changes
  useEffect(() => {
    enrichedKeysRef.current.clear();
  }, [activeProject?.key]);
  // Clear transition states after 3 seconds
  useEffect(() => {
    const entries = Object.entries(transitionStates);
    const completed = entries.filter(([_, s]) => s.status === 'success' || s.status === 'error');
    if (completed.length === 0) return;
    const t = setTimeout(() => {
      setTransitionStates(prev => {
        const next = {
          ...prev
        };
        completed.forEach(([key]) => delete next[key]);
        return next;
      });
    }, 3000);
    return () => clearTimeout(t);
  }, [transitionStates]);
  const [selectedIssueKey, setSelectedIssueKey] = useState<string | null>(null);
  const handleDragStart = (e: React.DragEvent, issueKey: string) => {
    e.dataTransfer.setData('issueKey', issueKey);
    setDraggingKey(issueKey);
  };
  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverColumn(columnId);
  };
  /** Execute Jira transition in background */
  const executeJiraTransition = async (issueKey: string, targetStatus: KanbanStatus) => {
    setTransitionStates(prev => ({
      ...prev,
      [issueKey]: {
        issueKey,
        status: 'pending'
      }
    }));
    try {
      const transResult = await runTool('get-transitions', {
        issue_key: issueKey
      });
      if (!transResult.success) {
        throw new Error(transResult.error || 'No se pudieron obtener las transiciones');
      }
      const transitions = transResult.data?.transitions || transResult.data?.values || transResult.data || [];
      if (!Array.isArray(transitions) || transitions.length === 0) {
        throw new Error('No hay transiciones disponibles para este issue');
      }
      const match = findMatchingTransition(transitions, targetStatus);
      if (!match) {
        const available = transitions.map((t: any) => t.name || t.to?.name).join(', ');
        throw new Error(`No se encontró transición a "${targetStatus}". Disponibles: ${available}`);
      }
      const execResult = await runTool('transition-issue', {
        issue_key: issueKey,
        transition_id: String(match.id)
      });
      if (!execResult.success) {
        throw new Error(execResult.error || 'Error al ejecutar la transición');
      }
      setTransitionStates(prev => ({
        ...prev,
        [issueKey]: {
          issueKey,
          status: 'success',
          message: `→ ${targetStatus}`
        }
      }));
      console.log(`[Kanban] ✓ ${issueKey} transitioned to ${targetStatus} via "${match.name}"`);
    } catch (err: any) {
      console.error(`[Kanban] ✗ ${issueKey} transition failed:`, err.message);
      setTransitionStates(prev => ({
        ...prev,
        [issueKey]: {
          issueKey,
          status: 'error',
          message: err.message
        }
      }));
    }
  };
  const handleDrop = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    const issueKey = e.dataTransfer.getData('issueKey');
    const currentIssue = boardIssues.find(i => i.key === issueKey);
    // Find the target column to determine the status to set
    const targetCol = effectiveColumns.find(c => c.id === columnId);
    // For optimistic update, use the first status name or the column id
    const newStatus = (targetCol?.statusNames[0] || columnId) as KanbanStatus;
    // Update local state immediately (optimistic)
    setBoardIssues(prev => prev.map(issue => issue.key === issueKey ? {
      ...issue,
      status: newStatus,
      rawStatus: targetCol?.statusNames[0] || columnId
    } : issue));
    // If live mode and status actually changed, execute Jira transition
    if (isLive && currentIssue && currentIssue.status !== newStatus) {
      executeJiraTransition(issueKey, newStatus);
    }
    setDragOverColumn(null);
    setDraggingKey(null);
  };
  const handleDragEnd = () => {
    setDragOverColumn(null);
    setDraggingKey(null);
  };
  /** Get issues that belong to a column — uses rawStatus for Jira columns */
  const getColumnIssues = (col: EffectiveColumn) => {
    const statusSet = new Set(col.statusNames.map(s => s.toLowerCase()));
    return filteredIssues.filter(i => {
      // 1. Match by rawStatus (original Jira status) — most accurate
      if (i.rawStatus) {
        const raw = i.rawStatus.toLowerCase();
        if (statusSet.has(raw)) return true;
        if (col.id.toLowerCase() === raw) return true;
      }
      // 2. Match by simplified status
      const issueStatus = i.status.toLowerCase();
      if (statusSet.has(issueStatus)) return true;
      if (col.id.toLowerCase() === issueStatus) return true;
      // 3. For Jira columns: use keyword matching to map simplified status → column
      if (isFromJira) {
        // Check if any of the column's real status names would produce this simplified status
        for (const statusName of col.statusNames) {
          const sn = statusName.toLowerCase();
          if (sn.includes('done') || sn.includes('closed') || sn.includes('resolved')) {
            if (issueStatus === 'done') return true;
          } else if (sn.includes('progress')) {
            if (issueStatus === 'in progress') return true;
          } else if (sn.includes('review') || sn.includes('testing') || sn.includes('qa')) {
            if (issueStatus === 'review') return true;
          } else if (sn.includes('backlog')) {
            if (issueStatus === 'backlog') return true;
          }
          // Don't match 'to do' here — it's the default and would match everything
        }
      }
      return false;
    });
  };
  const totalPoints = (col: EffectiveColumn) => getColumnIssues(col).reduce((sum, i) => sum + (i.points || 0), 0);
  // Count unmatched issues (not in any column)
  const matchedKeys = new Set(effectiveColumns.flatMap(col => getColumnIssues(col).map(i => i.key)));
  const unmatchedIssues = filteredIssues.filter(i => !matchedKeys.has(i.key));
  return <div className="h-full overflow-hidden flex flex-col animate-in fade-in duration-300" data-id="element-5153">
      {/* Board Header */}
      <div className="px-8 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between shrink-0" data-id="element-5154">
        <div className="flex items-center gap-3" data-id="element-5155">
          {boardName && <h2 className="text-lg font-bold text-slate-100 mr-2" data-id="element-5156">
              {boardName}
            </h2>}
          <p className="text-xs text-slate-500" data-id="element-5157">
            {boardIssues.length} issues ·{' '}
            {boardIssues.reduce((s, i) => s + (i.points || 0), 0)} pts total
          </p>
          {isLive && <span className="text-[10px] text-emerald-400 bg-emerald-950/40 border border-emerald-900/40 px-1.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-5158">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-5159"></span>
              Sincronizado con Jira
            </span>}
          {/* Column source badge */}
          {isLive && !columnsLoading && <span className={`text-[10px] px-1.5 py-0.5 rounded-full border flex items-center gap-1 ${isFromJira ? 'text-indigo-400 bg-indigo-950/30 border-indigo-900/40' : 'text-slate-500 bg-slate-800/50 border-slate-700/50'}`} data-id="element-5160">
              {isFromJira ? `${effectiveColumns.length} columnas · Jira` : 'Columnas inferidas'}
            </span>}
          {enrichingCount > 0 && <span className="text-[10px] text-cyan-400 bg-cyan-950/30 border border-cyan-900/40 px-1.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-5161">
              <Loader2 size={10} className="animate-spin" data-id="element-5162" />
              Cargando datos... ({enrichingCount} restantes)
            </span>}
          {unmatchedIssues.length > 0 && enrichingCount === 0 && <span className="text-[10px] text-amber-400 bg-amber-950/30 border border-amber-900/40 px-1.5 py-0.5 rounded-full" data-id="element-5163">
              {unmatchedIssues.length} sin columna
            </span>}
          <RefreshButton onRefresh={() => {
          clearBoardColumnsCache();
          const seen = new Set<string>();
          const combined: Issue[] = [];
          const issues = Array.isArray(activeProject?.issues) ? activeProject.issues : [];
          const backlog = Array.isArray(activeProject?.backlog) ? activeProject.backlog : [];
          for (const issue of [...issues, ...backlog]) {
            if (!seen.has(issue.key)) {
              seen.add(issue.key);
              combined.push(issue);
            }
          }
          setBoardIssues(combined);
        }} data-id="element-5164" />
        </div>
        <div className="flex items-center gap-3" data-id="element-5165">
          {Array.from(new Map(boardIssues.filter(i => i.assignee).map(i => [i.assignee!.name, i.assignee!])).values()).slice(0, 5).map(assignee => <div key={assignee.name} className={`w-7 h-7 rounded-full border flex items-center justify-center text-[10px] font-bold ${getAvatarColor(assignee.name)}`} title={assignee.name} data-id="element-5166">
                {assignee.initials}
              </div>)}
        </div>
      </div>

      {/* Filter Bar */}
      <BoardFilters issues={boardIssues} filters={filters} onFiltersChange={setFilters} filteredCount={filteredIssues.length} data-id="element-5167" />

      {/* Columns — with visible horizontal scrollbar */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden" style={{
      scrollbarColor: '#334155 transparent',
      scrollbarWidth: 'thin'
    }} data-id="element-5168">
        <div className="flex gap-4 p-6 h-full min-w-max" data-id="element-5169">
          {/* Skeleton columns while loading board configuration */}
          {columnsLoading && visibleColumns.length === 0 && <>
              {[1, 2, 3, 4].map(i => <div key={i} className="flex flex-col w-72 shrink-0 animate-pulse" data-id="element-5170">
                  <div className="flex items-center gap-2 mb-3 px-1" data-id="element-5171">
                    <div className="h-4 w-28 bg-slate-800 rounded" data-id="element-5172" />
                    <div className="h-4 w-6 bg-slate-800 rounded-full" data-id="element-5173" />
                  </div>
                  <div className="flex-1 rounded-xl bg-slate-900/30 p-2 space-y-2" data-id="element-5174">
                    {[1, 2, 3].map(j => <div key={j} className="bg-slate-900 border border-slate-800 rounded-lg p-3" data-id="element-5175">
                        <div className="flex items-center gap-1.5 mb-2" data-id="element-5176">
                          <div className="w-3 h-3 rounded bg-slate-700" data-id="element-5177" />
                          <div className="h-3 w-16 bg-slate-800 rounded" data-id="element-5178" />
                        </div>
                        <div className="space-y-1.5 mb-3" data-id="element-5179">
                          <div className="h-3.5 bg-slate-800 rounded w-full" data-id="element-5180" />
                          <div className="h-3.5 bg-slate-800 rounded w-2/3" data-id="element-5181" />
                        </div>
                        <div className="flex items-center justify-between" data-id="element-5182">
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-700" data-id="element-5183" />
                          <div className="w-6 h-6 rounded-full bg-slate-800" data-id="element-5184" />
                        </div>
                      </div>)}
                  </div>
                </div>)}
            </>}
          {visibleColumns.map(col => {
          const colIssues = getColumnIssues(col);
          const isDragOver = dragOverColumn === col.id;
          return <div key={col.id} className="flex flex-col w-72 shrink-0" onDragOver={e => handleDragOver(e, col.id)} onDrop={e => handleDrop(e, col.id)} onDragLeave={() => setDragOverColumn(null)} data-id="element-5185">
                {/* Column Header */}
                <div className="flex items-center justify-between mb-3 px-1" data-id="element-5186">
                  <div className="flex items-center gap-2" data-id="element-5187">
                    <span className={`text-sm font-semibold ${col.color}`} data-id="element-5188">
                      {col.label}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${col.count_color}`} data-id="element-5189">
                      {colIssues.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-5190">
                    <span className="text-[10px] text-slate-600" data-id="element-5191">
                      {totalPoints(col)}p
                    </span>
                    <button onClick={() => toggleColumnVisibility(col.id)} className="p-0.5 rounded text-slate-600 hover:text-slate-400 hover:bg-slate-800 transition-colors opacity-0 group-hover:opacity-100" style={{
                  opacity: effectiveColumns.length > 4 ? 1 : undefined
                }} title={`Ocultar ${col.label}`} data-id="element-5192">
                      <EyeOff size={12} data-id="element-5193" />
                    </button>
                  </div>
                </div>

                {/* Drop Zone */}
                <div className={`flex-1 rounded-xl border-2 border-dashed p-2 space-y-2 transition-all overflow-y-auto ${isDragOver ? 'border-indigo-500/50 bg-indigo-950/20' : 'border-transparent bg-slate-900/30'}`} data-id="element-5194">
                  {colIssues.map(issue => {
                // Show skeleton for issues that only have ID (not enriched yet)
                if (isSkeletonIssue(issue)) {
                  return <SkeletonCard key={issue.key} issueKey={issue.key} data-id="element-5195" />;
                }
                const linked = findLinkedGitData(issue.key, githubPrs, githubCommits, githubBranches);
                return <div key={issue.key} className={`transition-opacity ${draggingKey === issue.key ? 'opacity-40' : 'opacity-100'}`} data-id="element-5196">
                        <KanbanCard issue={issue} onDragStart={handleDragStart} onClick={() => setSelectedIssueKey(issue.key)} jiraBaseUrl={brand.jiraBaseUrl} transitionState={transitionStates[issue.key]} linkedGitData={linked} data-id="element-5197" />
                      </div>;
              })}

                  {/* Empty state */}
                  {colIssues.length === 0 && <div className={`h-20 rounded-lg border border-dashed flex items-center justify-center text-xs text-slate-600 ${isDragOver ? 'border-indigo-500/50' : 'border-slate-800'}`} data-id="element-5198">
                      {isDragOver ? 'Soltar aquí' : 'Sin issues'}
                    </div>}
                </div>
              </div>;
        })}

          {/* "Sin columna" column for unmatched issues */}
          {unmatchedIssues.length > 0 && enrichingCount === 0 && <div className="flex flex-col w-72 shrink-0" data-id="element-5199">
              <div className="flex items-center justify-between mb-3 px-1" data-id="element-5200">
                <div className="flex items-center gap-2" data-id="element-5201">
                  <span className="text-sm font-semibold text-amber-400" data-id="element-5202">
                    Sin columna
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-900/50 text-amber-400" data-id="element-5203">
                    {unmatchedIssues.length}
                  </span>
                </div>
                <span className="text-[10px] text-slate-600" data-id="element-5204">
                  {unmatchedIssues.reduce((s, i) => s + (i.points || 0), 0)}p
                </span>
              </div>
              <div className="flex-1 rounded-xl border-2 border-dashed border-amber-900/30 p-2 space-y-2 overflow-y-auto bg-amber-950/10" data-id="element-5205">
                {unmatchedIssues.map(issue => {
              if (isSkeletonIssue(issue)) {
                return <SkeletonCard key={issue.key} issueKey={issue.key} data-id="element-5206" />;
              }
              const linked = findLinkedGitData(issue.key, githubPrs, githubCommits, githubBranches);
              return <div key={issue.key} data-id="element-5207">
                      <KanbanCard issue={issue} onDragStart={handleDragStart} onClick={() => setSelectedIssueKey(issue.key)} jiraBaseUrl={brand.jiraBaseUrl} transitionState={transitionStates[issue.key]} linkedGitData={linked} data-id="element-5208" />

                      {/* Show the raw status so user knows why it's unmatched */}
                      {issue.rawStatus && <div className="mt-1 px-2" data-id="element-5209">
                          <span className="text-[9px] text-amber-500/70 italic" data-id="element-5210">
                            Status: {issue.rawStatus}
                          </span>
                        </div>}
                    </div>;
            })}
              </div>
            </div>}
        </div>
      </div>

      {/* Hidden columns bar */}
      {hiddenColumnsList.length > 0 && <div className="px-6 py-2 border-t border-slate-800 bg-slate-900/80 flex items-center gap-2 shrink-0" data-id="element-5211">
          <span className="text-[10px] text-slate-500 mr-1" data-id="element-5212">Ocultas:</span>
          {hiddenColumnsList.map(col => <button key={col.id} onClick={() => toggleColumnVisibility(col.id)} className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 transition-colors hover:bg-slate-800 ${col.count_color}`} data-id="element-5213">
              <Eye size={10} data-id="element-5214" />
              {col.label} ({getColumnIssues(col).length})
            </button>)}
        </div>}

      <IssueDetailPanel issueKey={selectedIssueKey} onClose={() => setSelectedIssueKey(null)} jiraBaseUrl={brand.jiraBaseUrl} data-id="element-5215" />
    </div>;
}